(function () {
  const { emit, on, prefersReducedMotion } = window.themeUtils || {};

  const SECTION_ID = "minicart";
  const cartPageSectionId =
    document.querySelector('[data-js="cart-page"]')?.dataset.sectionId || null;
  const SECTIONS_TO_FETCH = cartPageSectionId
    ? `${SECTION_ID},${cartPageSectionId}`
    : SECTION_ID;
  const DEBOUNCE_MS = 350;
  const FETCH_TIMEOUT_MS = 15000;
  const OPEN_PARAM = "minicart";
  const OPEN_PARAM_VALUE = "open";

  /**
   * The LineOps registry — one live op per cart line with outstanding
   * work, keyed by line-item key. A row's visual busy state is a pure
   * function of its op (or absence): nothing may clear it except (a) its
   * own batch settling with generation === snapshotGeneration, (b) its
   * own batch failing (rollback), (c) reassertion after a reconcile.
   * Value shape: { key, kind: 'quantity'|'remove'|'size', targetQty,
   * targetVariantId, baseQty, baseVariantId, state: 'pending'|'inflight'|
   * 'settling', generation, snapshotGeneration, batchId, rowEl }.
   */
  const lineOps = new Map();
  /** One shared debounce timer for the whole quantity batch. */
  let quantityFlushTimer = null;
  /** Single serialised mutation chain — at most one cart write in flight. */
  let mutationChain = Promise.resolve();
  /** In-flight mutation counter (drives the checkout-busy guard). */
  let mutationInFlight = 0;
  let discountErrorTimer = null;
  let refreshRequestToken = 0;
  /** Busy-state edge tracker — gates the idle totals fetch to busy→idle transitions only (§3.3). */
  let wasCartBusy = false;
  /** Module counter stamping each quantity batch sent to the server. */
  let batchIdCounter = 0;

  /** @returns {HTMLDialogElement|null} the live minicart dialog. */
  const getMinicart = () => document.getElementById(SECTION_ID);

  /** @returns {HTMLElement|null} the minicart header (carries data-text-* config). */
  const getHeader = () => document.querySelector(".minicart__header");

  /** @returns {boolean} whether the drawer is currently open. */
  const isMinicartOpen = () => getMinicart()?.dataset.state === "open";

  /**
   * Toggle a boolean data-attribute on an element.
   * @param {Element|null} el
   * @param {string} name - attribute name (e.g. 'data-error')
   * @param {boolean} on
   */
  const setBool = (el, name, on) => {
    if (!el) return;
    if (on) el.setAttribute(name, "true");
    else el.removeAttribute(name);
  };

  // ── Discount UI helpers ────────────────────────────────────

  const getDiscountUiElements = () => {
    const input = document.querySelector("[data-minicart-discount-input]");
    const error = document.querySelector("[data-minicart-discount-error]");
    const note = document.querySelector("[data-minicart-discount-note]");
    const panel = document.querySelector("[data-minicart-discount-panel]");
    return { input, error, note, panel, messageEl: error };
  };

  /**
   * Drive the discount panel's open / error state via data-attributes.
   * @param {Object} [state] - { open?: boolean, isError?: boolean, message?: string|null }
   */
  const setDiscountUiState = ({
    open = false,
    isError = false,
    message = null,
  } = {}) => {
    const { input, error, note, panel, messageEl } = getDiscountUiElements();

    setBool(input, "data-error", isError);
    setBool(error, "data-hidden", !isError);
    setBool(note, "data-hidden", isError);

    const shouldOpen = open || isError;
    setBool(panel, "data-open", shouldOpen);

    if (isError && message && messageEl) {
      messageEl.textContent = message;
    }
  };

  // ── Aria-live announcements ────────────────────────────────
  // Targets the static SR region inside the panel (a live region
  // outside an open modal dialog is inert to AT). It sits outside every
  // swap region, so it survives refreshes.

  /**
   * Announce a message to the minicart's polite live region.
   * @param {string} msg
   */
  const announce = (msg) => {
    const sr = document.querySelector("[data-minicart-sr-status]");
    if (!sr) return;
    sr.textContent = "";
    requestAnimationFrame(() => {
      sr.textContent = msg;
    });
  };

  // ── Error feedback ─────────────────────────────────────────

  /**
   * Hide the drawer error banner (animated unless reduced motion).
   * @param {HTMLElement|null} banner
   */
  const hideBanner = (banner) => {
    if (!banner || banner.hidden) return;
    if (prefersReducedMotion) {
      banner.hidden = true;
      banner.removeAttribute("data-dismissing");
      return;
    }
    banner.setAttribute("data-dismissing", "true");
    banner.addEventListener(
      "animationend",
      () => {
        banner.hidden = true;
        banner.removeAttribute("data-dismissing");
      },
      { once: true },
    );
  };

  let bannerDismissTimer = null;

  /**
   * Show the drawer-level error banner and announce it (once).
   * @param {string} message
   * @param {Object} [opts] - { duration?: number }
   */
  const showBannerError = (message, { duration = 6000 } = {}) => {
    const minicart = getMinicart();
    const banner = minicart?.querySelector("[data-minicart-banner]");
    const messageEl = banner?.querySelector("[data-minicart-banner-message]");
    if (!banner || !messageEl) return;

    // Anchor the banner below the shipping bar when it's showing (else below
    // the header) so the toast never overlaps the free-shipping bar.
    const shippingBar = minicart.querySelector(".shipping-bar");
    const anchor =
      shippingBar && !shippingBar.hidden && shippingBar.offsetParent
        ? shippingBar
        : minicart.querySelector(".minicart__header");
    if (anchor) {
      const top = anchor.offsetTop + anchor.offsetHeight + 8;
      banner.style.setProperty("--banner-top", `${top}px`);
    }

    banner.removeAttribute("data-dismissing");
    const decodedMessage = decodeHtmlEntities(message);
    messageEl.textContent = decodedMessage;
    banner.hidden = false;
    announce(decodedMessage);

    const dismissBtn = banner.querySelector("[data-minicart-banner-dismiss]");
    if (dismissBtn && !dismissBtn.dataset.bound) {
      dismissBtn.addEventListener("click", () => {
        if (bannerDismissTimer) {
          clearTimeout(bannerDismissTimer);
          bannerDismissTimer = null;
        }
        hideBanner(banner);
      });
      dismissBtn.dataset.bound = "true";
    }

    if (bannerDismissTimer) clearTimeout(bannerDismissTimer);
    if (duration > 0) {
      bannerDismissTimer = setTimeout(() => {
        hideBanner(banner);
        bannerDismissTimer = null;
      }, duration);
    }
  };

  /**
   * Dismiss any lingering error banner after a mutation succeeds, so a stale
   * failure message never sits over a now-correct cart.
   * @param {HTMLElement|null} [minicart]
   */
  const clearStaleBanner = (minicart) => {
    const banner = (minicart || getMinicart())?.querySelector(
      "[data-minicart-banner]",
    );
    if (!banner) return;
    if (bannerDismissTimer) {
      clearTimeout(bannerDismissTimer);
      bannerDismissTimer = null;
    }
    hideBanner(banner);
  };

  /**
   * Show a per-line error (falls back to the banner if the line is gone).
   * Announces once, via announce() inside this function only.
   * @param {string} lineKey - cart line key (or legacy variant id)
   * @param {string} message
   * @returns {boolean} whether an item-level error was shown
   */
  const showItemError = (lineKey, message) => {
    const minicart = getMinicart();
    if (!minicart) return false;

    let item = minicart.querySelector(
      `.minicart__item[data-key="${CSS.escape(String(lineKey))}"]`,
    );
    if (!item) {
      item = minicart.querySelector(
        `.minicart__item[data-id="${CSS.escape(String(lineKey))}"]`,
      );
    }
    if (!item) {
      showBannerError(message);
      return false;
    }

    const errorEl = item.querySelector("[data-minicart-item-error]");
    const messageEl = item.querySelector("[data-minicart-item-error-message]");
    const decodedMessage = decodeHtmlEntities(message);
    if (errorEl && messageEl) {
      messageEl.textContent = decodedMessage;
      errorEl.hidden = false;
    }

    item.setAttribute("data-error", "true");

    try {
      item.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "nearest",
      });
    } catch {
      item.scrollIntoView();
    }

    announce(decodedMessage);
    return true;
  };

  // ── Cart badge animation ───────────────────────────────────

  /**
   * Bump the cart count badge in BOTH the drawer header and the page
   * header. Uses `scale` so it composes with the badges' existing
   * transforms / positioning.
   */
  const bumpCartBadge = () => {
    if (prefersReducedMotion) return;
    const targets = [
      document.querySelector(".minicart__cart-button"),
      document.querySelector(".icons__cart"),
    ];
    targets.forEach((el) => {
      if (!el) return;
      el.removeAttribute("data-bumping");
      void el.offsetWidth; // reflow so the animation can replay
      el.setAttribute("data-bumping", "true");
      el.addEventListener(
        "animationend",
        () => el.removeAttribute("data-bumping"),
        { once: true },
      );
    });
  };

  // ── Money formatting ───────────────────────────────────────
  // Formats the presentment currency already implied by
  // `shop.money_format` — it must never invent a currency symbol.

  // Literal double-braces are hex-escaped so Liquid doesn't eat them.
  const MONEY_PLACEHOLDER = /\x7b\x7b\s*(\w+)\s*\x7d\x7d/;

  /**
   * Insert a thousands separator into an integer string.
   * @param {string} intStr
   * @param {string} sep
   * @returns {string}
   */
  const addThousands = (intStr, sep) =>
    intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

  /**
   * Format an amount in cents against the shop money format, matching
   * Shopify's server-side `money` filter (all five placeholders +
   * thousands separators). Strips any HTML wrapper the format carries.
   * @param {number} cents
   * @returns {string}
   */
  const formatMoney = (cents) => {
    const fmt =
      window.theme?.moneyFormat ||
      document.querySelector("[data-money-format]")?.dataset.moneyFormat ||
      "\x7b\x7bamount\x7d\x7d";
    const value = cents / 100;
    const withDot = value.toFixed(2);
    const dotInt = withDot.split(".")[0];
    const dotFrac = withDot.split(".")[1];
    const noDec = Math.round(value).toString();
    const formats = {
      amount: `${addThousands(dotInt, ",")}.${dotFrac}`,
      amount_no_decimals: addThousands(noDec, ","),
      amount_with_comma_separator: `${addThousands(dotInt, ".")},${dotFrac}`,
      amount_no_decimals_with_comma_separator: addThousands(noDec, "."),
      amount_with_space_separator: `${addThousands(dotInt, " ")}.${dotFrac}`,
    };
    const match = fmt.match(MONEY_PLACEHOLDER);
    const key = match && formats[match[1]] !== undefined ? match[1] : "amount";
    const text = fmt.replace(MONEY_PLACEHOLDER, formats[key]);
    // Strip any <span> wrapper the shop money format carries, via
    // DOMParser (no innerHTML) — the format is a server-provided string.
    return decodeHtmlEntities(text).trim();
  };

  // ── Shipping bar + instalment sync ─────────────────────────
  // The shipping bar is rendered server-side from a snippet that depends
  // on a metaobject reference. Section Rendering API responses sometimes
  // omit this content, so we persist the element across DOM swaps and
  // update its state from the latest cart total in JS.

  /**
   * Decode HTML entities / strip tags via DOMParser.
   * @param {string} str
   * @returns {string}
   */
  const decodeHtmlEntities = (str) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str || "", "text/html");
    return doc.documentElement.textContent;
  };

  /**
   * Sync the free-shipping progress bar from the latest cart total, and
   * fire the free-shipping celebration on a genuine below→above crossing.
   * @param {number} totalCents
   */
  const syncShippingBar = (totalCents) => {
    const bar = document.querySelector('[data-js="shipping-bar"]');
    if (!bar || typeof totalCents !== "number") return;

    // `data-syncing` is owned exclusively by syncBusyUI() (§3.3) — this
    // function runs mid-flight from several settle paths, so clearing the
    // attribute here would flicker the shared loading bar off and back on
    // whenever another op is still outstanding at settle time.

    const threshold = parseInt(bar.dataset.thresholdCents, 10);
    if (!threshold || isNaN(threshold)) return;

    const remaining = Math.max(0, threshold - totalCents);
    const achieved = remaining <= 0;
    let percent = (totalCents * 100) / threshold;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;

    const wasAchieved = bar.dataset.achieved === "true";
    bar.dataset.achieved = achieved ? "true" : "false";

    const fill = bar.querySelector('[data-js="shipping-bar-fill"]');
    if (fill) fill.style.width = `${percent}%`;

    // Gate the fill's transition behind `data-live`, stamped after this
    // first sync — the very first JS-driven update (page load / drawer
    // reveal) lands instantly instead of sweeping from the server-rendered
    // width; every sync after this one already carries the flag, so it
    // transitions from its previous value as intended (§4).
    if (!bar.dataset.live) bar.dataset.live = "true";

    if (!achieved) {
      const progressEl = bar.querySelector(
        '[data-js="shipping-bar-in-progress"]',
      );
      if (progressEl) {
        const tpl = decodeHtmlEntities(bar.dataset.inProgressTemplate || "");
        if (tpl) {
          // Server-provided rich-text template — documented innerHTML exception.
          progressEl.innerHTML = tpl.replaceAll(
            "[amount]",
            formatMoney(remaining),
          );
        }
      }
    }

    // E3 celebration — only on a real false→true crossing (never on a
    // server-rendered achieved state), and only when motion is allowed.
    if (achieved && !wasAchieved && !prefersReducedMotion) {
      bar.setAttribute("data-celebrating", "true");
      bar.addEventListener("animationend", function onCelebrationEnd(e) {
        if (e.animationName !== "shipping-bar-celebrate-message") return;
        bar.removeAttribute("data-celebrating");
        bar.removeEventListener("animationend", onCelebrationEnd);
      });
    }
  };

  /**
   * Sync BNPL instalment amounts (total ÷ divisor) to the latest total.
   * @param {number} totalCents
   */
  const syncInstalments = (totalCents) => {
    if (typeof totalCents !== "number") return;
    document.querySelectorAll("[data-minicart-instalment]").forEach((el) => {
      const divisor = parseInt(el.dataset.divisor, 10) || 4;
      el.textContent = formatMoney(Math.round(totalCents / divisor));
    });
  };

  /** Latest-wins ordering for totals syncs — stale responses are dropped. */
  let totalsRequestToken = 0;

  const fetchAndSyncCartTotals = async () => {
    const token = ++totalsRequestToken;
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (token !== totalsRequestToken) return;
      if (typeof data.total_price === "number") {
        syncShippingBar(data.total_price);
        syncInstalments(data.total_price);
      }
      syncShippingDiscountTag(data);
    } catch {
      /* noop */
    } finally {
      // A superseded call must not clobber a newer call's syncing state —
      // syncBusyUI() re-derives the honest busy state either way.
      if (token === totalsRequestToken) syncBusyUI();
    }
  };

  // ── Optimistic UI ──────────────────────────────────────────

  /**
   * Roll back one line's optimistic change on genuine failure (§3.4) — the
   * only other path (besides its own batch settling) allowed to clear a
   * row's busy visuals.
   * @param {Object} op - a lineOps entry
   */
  const revertOptimisticUpdate = (op) => {
    const row = op?.rowEl;
    if (!row) return;

    if (op.kind === "quantity") {
      const qtyInput = row.querySelector("[data-minicart-quantity-input]");
      if (qtyInput) qtyInput.value = op.baseQty;
      row.querySelectorAll("[data-minicart-quantity]").forEach((b) => {
        b.dataset.currentQty = op.baseQty;
      });
      delete row.dataset.pending;
      return;
    }

    if (op.kind === "size") {
      const sel = row.querySelector("[data-minicart-size-select]");
      if (sel && op.baseVariantId != null) sel.value = op.baseVariantId;
      delete row.dataset.pending;
      return;
    }

    // kind === 'remove' — restore the row with the expand animation (§4);
    // reverse the optimistic empty-state transition if it fired.
    restoreRemovedRow(row);
    reverseTransitionToEmpty();
  };

  const applyOptimisticUpdate = (itemEl, newQty) => {
    if (!itemEl) return;
    const qtyInput = itemEl.querySelector("[data-minicart-quantity-input]");
    if (qtyInput) qtyInput.value = newQty;
    itemEl.querySelectorAll("[data-minicart-quantity]").forEach((b) => {
      b.dataset.currentQty = newQty;
    });
    itemEl.dataset.pending = "true";
  };

  /**
   * Apply/reassert a live op's visuals onto its row. Idempotent — safe to
   * call repeatedly (e.g. after every reconcile) without stacking effects.
   * @param {Object} op - a lineOps entry
   */
  const applyOpVisuals = (op) => {
    const row = op?.rowEl;
    if (!row) return;
    if (op.kind === "remove") {
      if (!row.dataset.removing) animateItemRemoval(row);
      return;
    }
    if (op.kind === "quantity") {
      applyOptimisticUpdate(row, op.targetQty);
      return;
    }
    if (op.kind === "size") {
      row.dataset.pending = "true";
    }
  };

  /**
   * Re-resolve every live (non-settling) op's row by key — variant-id
   * fallback for size ops — and reassert its visuals. Called after every
   * reconcile (§3.5) — the registry-based replacement for the old
   * pending-map reapplication helper.
   */
  const reassertOpVisuals = () => {
    const minicart = getMinicart();
    if (!minicart || lineOps.size === 0) return;
    lineOps.forEach((op) => {
      if (op.state === "settling") return;
      let row = op.rowEl && op.rowEl.isConnected ? op.rowEl : null;
      if (!row)
        row = minicart.querySelector(
          `.minicart__item[data-key="${CSS.escape(op.key)}"]`,
        );
      if (!row) {
        const variantId = op.targetVariantId || op.baseVariantId;
        if (variantId)
          row = minicart.querySelector(
            `.minicart__item[data-id="${CSS.escape(variantId)}"]`,
          );
      }
      if (!row) return; // line genuinely gone — nothing to reassert
      op.rowEl = row;
      applyOpVisuals(op);
    });
  };

  /**
   * Measure and mark a row for the removal collapse (§5.9b). The
   * collapse plays during the network flight; on height-transition end
   * the row is display:none'd so its flex gap disappears on slow networks.
   * Cleans up on animationend/animationcancel + a timeout fallback (§4
   * interruptibility rule) so an interrupted collapse (e.g. a rollback
   * racing the animation) never leaves a dangling listener behind.
   * @param {HTMLElement|null} itemEl
   */
  const animateItemRemoval = (itemEl) => {
    if (!itemEl) return;
    // Read (measure) before write, in that order, so this never forces an
    // extra synchronous layout: the collapse keyframe needs an explicit
    // start height (a height transition from `auto` won't animate).
    const height = itemEl.offsetHeight;
    // A row mid fresh-settle fade shares .minicart__item's `animation`
    // property with the collapse keyframes below — clear it first so the
    // collapse always wins the cascade instead of being silently
    // overridden by a still-playing fade (§4 interruptibility rule).
    itemEl.removeAttribute("data-fresh");
    itemEl.style.setProperty("--item-height", `${height}px`);
    itemEl.dataset.removing = "true";
    if (prefersReducedMotion) return; // CSS[data-removing] hides it instantly
    // On collapse-end, display:none the row so the .minicart__items flex gap
    // (which a zero-height child still occupies) disappears on slow networks.
    let fallback;
    const cleanup = () => {
      clearTimeout(fallback);
      itemEl.removeEventListener("animationend", onCollapseEnd);
      itemEl.removeEventListener("animationcancel", cleanup);
    };
    function onCollapseEnd(e) {
      if (e.animationName !== "minicart-item-collapse") return;
      itemEl.dataset.collapsed = "true";
      cleanup();
    }
    itemEl.addEventListener("animationend", onCollapseEnd);
    itemEl.addEventListener("animationcancel", cleanup);
    fallback = setTimeout(() => {
      itemEl.dataset.collapsed = "true";
      cleanup();
    }, 400); // above the ~300ms collapse duration
  };

  /**
   * Restore a collapsed/collapsing row on rollback (§3.4, §4) — the
   * `--item-height` custom property set by animateItemRemoval() is reused
   * as the expand target so no re-measurement of a zero-height row is
   * needed. Cleans up on animationend/animationcancel + a 400ms timeout
   * fallback, kept as a deliberate safety net for cases where the
   * animation event never fires (drawer closed mid-restore, reduced
   * motion, or a dropped animationcancel).
   * @param {HTMLElement} row
   */
  const restoreRemovedRow = (row) => {
    if (!row.dataset.removing && !row.dataset.collapsed) return; // nothing to restore
    delete row.dataset.collapsed;
    delete row.dataset.removing;
    if (prefersReducedMotion) {
      row.style.removeProperty("--item-height");
      return;
    }
    row.setAttribute("data-restoring", "true");
    let fallback;
    const cleanup = () => {
      clearTimeout(fallback);
      row.removeAttribute("data-restoring");
      row.style.removeProperty("--item-height");
      row.removeEventListener("animationend", onRestoreEnd);
      row.removeEventListener("animationcancel", cleanup);
    };
    function onRestoreEnd(e) {
      if (e.animationName !== "minicart-item-expand") return;
      cleanup();
    }
    row.addEventListener("animationend", onRestoreEnd);
    row.addEventListener("animationcancel", cleanup);
    fallback = setTimeout(cleanup, 400); // above the ~320ms spec'd duration
  };

  /**
   * Reverse the optimistic items→empty transition (transitionToEmpty) on
   * a failed last-line removal — a no-op if that transition never fired.
   * Sequenced per §4 ("empty state fades out first so the two never
   * overlap"): the empty state plays minicart-empty-out, and only once
   * it's clear (animationend/animationcancel/timeout) does the footer,
   * items list and shipping bar return. A concurrent refreshMinicart()
   * may also re-assert this same chrome directly (its own belt-and-
   * suspenders reset) — both converge on the same end state, this just
   * makes the common (network-failure) path animate instead of snap.
   */
  const reverseTransitionToEmpty = () => {
    const minicart = getMinicart();
    const body = minicart?.querySelector(".minicart__body");
    if (!body?.dataset.emptying) return;
    const empty = body.querySelector("[data-minicart-empty-state]");
    const footer = minicart.querySelector("footer");
    const shippingBar = minicart.querySelector(".shipping-bar");

    const reveal = () => {
      delete body.dataset.emptying;
      if (footer) delete footer.dataset.leaving;
      if (shippingBar) shippingBar.hidden = false;
    };

    if (!empty || empty.hidden || prefersReducedMotion) {
      if (empty) {
        empty.hidden = true;
        empty.removeAttribute("data-entering");
      }
      reveal();
      return;
    }

    if (empty.dataset.leaving) return; // already reversing — one pass is enough

    empty.removeAttribute("data-entering");
    empty.setAttribute("data-leaving", "true");
    let fallback;
    const cleanup = () => {
      clearTimeout(fallback);
      empty.hidden = true;
      empty.removeAttribute("data-leaving");
      empty.removeEventListener("animationend", onLeaveEnd);
      empty.removeEventListener("animationcancel", cleanup);
      reveal();
    };
    function onLeaveEnd(e) {
      if (e.animationName !== "minicart-empty-out") return;
      cleanup();
    }
    empty.addEventListener("animationend", onLeaveEnd);
    empty.addEventListener("animationcancel", cleanup);
    fallback = setTimeout(cleanup, 350); // above the --motion-exit (190ms) duration
  };

  // ── Mutation pipeline (batched + serialised) ───────────────

  /**
   * Serialise a cart mutation onto the single chain. At most one cart
   * write is ever in flight; nothing aborts anything.
   * @param {() => Promise<*>} run
   * @returns {Promise<*>}
   */
  const queueCartMutation = (run) => {
    mutationInFlight++;
    syncBusyUI();
    mutationChain = mutationChain.then(run, run).finally(() => {
      mutationInFlight--;
      syncBusyUI();
    });
    return mutationChain;
  };

  /** @returns {boolean} whether cart work is genuinely outstanding. */
  const isCartBusy = () => lineOps.size > 0 || mutationInFlight > 0;

  /**
   * Derive the shared busy chrome (loading bar + shipping-bar dim) from
   * the registry + in-flight mutation count (§3.3) — the single function
   * every arming/clearing site routes through. The checkout button is
   * never dimmed/disabled by background syncing (T3, §1 decision 4) — its
   * own `[data-awaiting]` state (set only by an actual checkout click) is
   * the sole visual feedback it shows.
   *
   * The idle-totals fetch only fires on a genuine busy→idle edge
   * (wasCartBusy) — fetchAndSyncCartTotals()'s own finally re-enters this
   * function to re-derive the (already-idle) syncing attributes, and
   * without the edge check that re-entry would start another fetch every
   * time, looping forever once the cart goes idle.
   */
  const syncBusyUI = () => {
    const minicart = getMinicart();
    const bar = document.querySelector('[data-js="shipping-bar"]');
    const busy = isCartBusy();

    if (busy) {
      if (minicart) minicart.dataset.syncing = "true";
      if (bar) bar.dataset.syncing = "true";
    } else {
      if (minicart) delete minicart.dataset.syncing;
      if (bar) delete bar.dataset.syncing;
      if (wasCartBusy) fetchAndSyncCartTotals();
    }
    wasCartBusy = busy;
  };

  /**
   * Relocate focus at removal-INTENT time (§5, T5) — before
   * animateItemRemoval()/transitionToEmpty() flip any CSS that display:none's
   * the row or (last line) the whole items list. Both happen synchronously
   * under reduced motion, and even with motion the collapse's display:none
   * lands well before the debounced flush and its settle-time
   * captureRowFocus()/restoreRowFocus() pair ever run — so focus must be
   * moved here or it silently drops to <body>. No-op if focus isn't
   * currently inside the row (or, for the last line, the items list) about
   * to disappear. Ladder: next live row's remove button → previous live
   * row's remove button → empty state's primary link → dialog title.
   * @param {HTMLElement} itemEl - the row being queued for removal
   * @param {boolean} isLastLine - true when transitionToEmpty() is about to
   *   hide the entire items list (no row survives to receive focus)
   */
  const relocateFocusForRemoval = (itemEl, isLastLine) => {
    const active = document.activeElement;
    const scope = isLastLine
      ? itemEl?.closest(".minicart__items") || itemEl
      : itemEl;
    if (!active || !scope || !scope.contains(active)) return;

    const minicart = getMinicart();
    if (!minicart) return;

    if (!isLastLine) {
      const rows = [
        ...minicart.querySelectorAll(".minicart__items > .minicart__item"),
      ];
      const index = rows.indexOf(itemEl);
      const isLive = (row) =>
        row &&
        row !== itemEl &&
        lineOps.get(row.dataset.key)?.kind !== "remove" &&
        !row.dataset.removing;
      const next = rows.slice(index + 1).find(isLive);
      const prev = [...rows.slice(0, Math.max(index, 0))]
        .reverse()
        .find(isLive);
      const target = next || prev;
      if (target) {
        const control =
          target.querySelector("[data-minicart-remove]") || target;
        control.focus({ preventScroll: true });
        return;
      }
    }

    const emptyState = minicart.querySelector("[data-minicart-empty-state]");
    const emptyLink =
      emptyState && !emptyState.hidden
        ? emptyState.querySelector("a, button")
        : null;
    if (emptyLink) {
      emptyLink.focus({ preventScroll: true });
      return;
    }

    const title = minicart.querySelector("#minicart-title");
    if (title) title.focus({ preventScroll: true });
  };

  /**
   * Queue a quantity change for the shared batch (qty ≤ 0 removes). A new
   * intent on a line with a live op mutates that op in place instead of
   * clearing it — baseQty (the rollback value) is left untouched (§3.1).
   * @param {HTMLElement} itemEl
   * @param {string} lineKey
   * @param {number} newQty
   */
  const queueQuantity = (itemEl, lineKey, newQty) => {
    const qty = Math.max(0, newQty);
    const kind = qty <= 0 ? "remove" : "quantity";

    // Idempotent remove (D4): a removal already queued/in-flight for this
    // line is a no-op. Without this guard, a double press re-enters with
    // the row already mid-collapse, double-counts it out of the last-line
    // check below, and can flip the drawer to a false empty state.
    if (kind === "remove" && lineOps.get(lineKey)?.kind === "remove") return;

    let op = lineOps.get(lineKey);
    if (!op) {
      const qtyInput = itemEl.querySelector("[data-minicart-quantity-input]");
      const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
      op = {
        key: lineKey,
        kind,
        targetQty: qty,
        targetVariantId: null,
        baseQty: currentQty,
        baseVariantId: null,
        state: "pending",
        generation: 0,
        snapshotGeneration: -1,
        batchId: null,
        rowEl: itemEl,
      };
      lineOps.set(lineKey, op);
    } else {
      op.kind = kind;
      op.targetQty = qty;
      op.rowEl = itemEl;
    }
    op.state = "pending";
    op.generation += 1;

    if (qty <= 0) {
      const minicart = getMinicart();
      const rows = minicart
        ? [...minicart.querySelectorAll(".minicart__item")]
        : [];
      // Registry-based, not data-removing-based (D4): a row already marked
      // data-removing from an earlier intent must still count as "will be
      // gone" here, but so must every OTHER row concurrently queued for
      // removal — checking the DOM attribute alone undercounts concurrent
      // removals and overcounts a lone double-press. Transition to empty
      // only once every remaining row carries its own remove op.
      const anyRemaining = rows.some(
        (row) => lineOps.get(row.dataset.key)?.kind !== "remove",
      );
      // Relocate focus BEFORE either path flips CSS that display:none's the
      // row (or, last line, the whole items list) — see relocateFocusForRemoval.
      relocateFocusForRemoval(itemEl, !anyRemaining);
      if (!anyRemaining) transitionToEmpty();
      else animateItemRemoval(itemEl);
    } else {
      applyOptimisticUpdate(itemEl, qty);
    }

    syncBusyUI();

    if (quantityFlushTimer) clearTimeout(quantityFlushTimer);
    quantityFlushTimer = setTimeout(flushQuantities, DEBOUNCE_MS);
  };

  /**
   * Optimistically flip the drawer to its empty state while the last-line
   * removal is in flight — the empty state (already in the DOM, hidden)
   * crossfades in as the items + footer fade out. The subsequent refresh
   * reconciles: server empty → matches; server still has items (failed
   * removal) → the fresh items return and the empty state hides again.
   */
  const transitionToEmpty = () => {
    const minicart = getMinicart();
    if (!minicart) return;
    const body = minicart.querySelector(".minicart__body");
    const empty = body?.querySelector("[data-minicart-empty-state]");
    const footer = minicart.querySelector("footer");
    const shippingBar = minicart.querySelector(".shipping-bar");

    // Hide the outgoing items + recommendations rail instantly so only the
    // empty state remains — no flash of the old cart behind it. (The refresh
    // clears data-emptying; on a failed removal the fresh items reappear.)
    if (body) body.dataset.emptying = "true";
    if (footer) footer.dataset.leaving = "true";
    if (shippingBar) shippingBar.hidden = true;

    if (empty) {
      empty.hidden = false;
      if (!prefersReducedMotion) {
        empty.setAttribute("data-entering", "true");
        // animationend + animationcancel + timeout fallback (§4
        // interruptibility rule) — a fast rollback can remove
        // data-entering (reverseTransitionToEmpty) before it finishes.
        let fallback;
        const cleanup = () => {
          clearTimeout(fallback);
          empty.removeAttribute("data-entering");
          empty.removeEventListener("animationend", onEnterEnd);
          empty.removeEventListener("animationcancel", cleanup);
        };
        function onEnterEnd(e) {
          if (e.animationName !== "minicart-empty-in") return;
          cleanup();
        }
        empty.addEventListener("animationend", onEnterEnd);
        empty.addEventListener("animationcancel", cleanup);
        fallback = setTimeout(cleanup, 450); // above the 0.4s entering duration
      }
    }
  };

  /**
   * Snapshot every pending op into a new batch and queue the flush. Ops
   * are stamped with the batch id + their current generation but never
   * deleted here (§3.1) — only their own batch's settle/failure deletes
   * them. Safe to call early (e.g. from the checkout guard).
   */
  function flushQuantities() {
    if (quantityFlushTimer) {
      clearTimeout(quantityFlushTimer);
      quantityFlushTimer = null;
    }
    const pending = [...lineOps.values()].filter(
      (op) => op.state === "pending",
    );
    if (pending.length === 0) return;

    const batchId = ++batchIdCounter;
    const snapshot = new Map();
    pending.forEach((op) => {
      op.state = "inflight";
      op.batchId = batchId;
      op.snapshotGeneration = op.generation;
      snapshot.set(op.key, op.targetQty);
    });

    queueCartMutation(() => runQuantityFlush(batchId, snapshot));
  }

  /**
   * POST one batched cart/update.js for a batch's snapshot, refresh the
   * DOM, detect silent clamps, then settle every op the batch carried
   * (§3.2): unchanged-generation ops are deleted (success) or rolled back
   * and deleted (failure); ops whose generation moved on mid-flight are
   * left pending with a fresh baseQty from the server response.
   * @param {number} batchId
   * @param {Map<string, number>} snapshot
   */
  const runQuantityFlush = async (batchId, snapshot) => {
    const minicart = getMinicart();
    const updates = Object.fromEntries(snapshot);
    const data = await cartFetch("cart/update.js", { updates });
    const batchOps = () =>
      [...lineOps.values()].filter((op) => op.batchId === batchId);

    if (!data || data.error) {
      batchOps().forEach((op) => {
        if (op.generation !== op.snapshotGeneration) return; // superseded — leave pending
        revertOptimisticUpdate(op);
        lineOps.delete(op.key);
      });
      // Server is the source of truth — refresh from it and surface the
      // error AFTER the refresh completes so nothing wipes it (D8).
      const token = ++refreshRequestToken;
      await refreshMinicart(minicart, null, null, null, token);
      showBannerError(extractErrorMessage(data));
      syncBusyUI();
      return;
    }

    batchOps().forEach((op) => {
      if (op.generation === op.snapshotGeneration) op.state = "settling";
    });

    const token = ++refreshRequestToken;
    await refreshMinicart(minicart, null, data.sections, data, token, batchId);

    detectClamp(snapshot, data);

    const byKey = new Map(
      (data.items || []).map((item) => [item.key, item.quantity]),
    );
    const settledKeys = [];
    batchOps().forEach((op) => {
      if (op.generation === op.snapshotGeneration) {
        lineOps.delete(op.key);
        if (op.kind === "quantity") settledKeys.push(op.key);
      } else {
        const freshQty = byKey.get(op.key);
        if (typeof freshQty === "number") op.baseQty = freshQty;
        op.state = "pending";
      }
    });

    stampFreshRows(settledKeys);
    announceFlush(snapshot);
    clearStaleBanner(minicart);
    syncBusyUI();
  };

  /**
   * After a flush, compare requested quantities against the returned cart
   * and surface a per-line notice for any silently-clamped line.
   * @param {Map<string, number>} snapshot
   * @param {Object} cartData
   */
  const detectClamp = (snapshot, cartData) => {
    if (!cartData || !Array.isArray(cartData.items)) return;
    const byKey = new Map(cartData.items.map((i) => [i.key, i.quantity]));
    const message =
      getHeader()?.dataset.textQuantityAdjusted ||
      "Not enough stock available.";
    snapshot.forEach((requestedQty, lineKey) => {
      if (requestedQty <= 0) return; // removals aren't clamps
      const actual = byKey.get(lineKey);
      if (typeof actual === "number" && actual < requestedQty) {
        showItemError(lineKey, message);
      }
    });
  };

  /**
   * Fade in the rows whose op just settled successfully. Skips any key
   * that still holds a live op (generation moved on mid-flight) — no
   * fade restarts mid-interaction.
   * @param {string[]} settledKeys
   */
  const stampFreshRows = (settledKeys) => {
    if (prefersReducedMotion) return;
    const minicart = getMinicart();
    if (!minicart) return;
    settledKeys.forEach((lineKey) => {
      if (lineOps.has(lineKey)) return;
      const el = minicart.querySelector(
        `.minicart__item[data-key="${CSS.escape(lineKey)}"]`,
      );
      if (!el) return;
      el.setAttribute("data-fresh", "true");
      // animationend + animationcancel + timeout fallback (§4
      // interruptibility rule) — animateItemRemoval() also clears
      // data-fresh directly on its own interruption path (a remove
      // queued for this row before the fade finishes), so this is
      // belt-and-suspenders for any other future interrupter.
      let fallback;
      const cleanup = () => {
        clearTimeout(fallback);
        el.removeAttribute("data-fresh");
        el.removeEventListener("animationend", onFreshEnd);
        el.removeEventListener("animationcancel", cleanup);
      };
      function onFreshEnd(e) {
        if (e.animationName !== "minicart-item-fresh") return;
        cleanup();
      }
      el.addEventListener("animationend", onFreshEnd);
      el.addEventListener("animationcancel", cleanup);
      fallback = setTimeout(cleanup, 300); // above the 240ms fresh-fade duration
    });
  };

  /**
   * Announce the outcome of a flush (removal vs quantity update), once.
   * @param {Map<string, number>} snapshot
   */
  const announceFlush = (snapshot) => {
    const header = getHeader();
    const removedText =
      header?.dataset.textItemRemoved || "Item removed from bag";
    const updatedTpl =
      header?.dataset.textQuantityUpdated || "Quantity updated";
    const entries = [...snapshot.entries()];
    const allRemoval = entries.every(([, q]) => q <= 0);
    if (allRemoval) {
      announce(removedText);
      return;
    }
    if (entries.length === 1) {
      announce(
        updatedTpl.replace(/\x7b\x7b\s*quantity\s*\x7d\x7d/, entries[0][1]),
      );
      return;
    }
    announce(updatedTpl.replace(/\x7b\x7b\s*quantity\s*\x7d\x7d/, "").trim());
  };

  // ── Event binding ──────────────────────────────────────────

  function attachMinicartListeners() {
    const minicart = getMinicart();
    if (!minicart) return;

    const quantityBtns = minicart.querySelectorAll("[data-minicart-quantity]");
    const quantityInputs = minicart.querySelectorAll(
      "[data-minicart-quantity-input]",
    );
    const removeBtns = minicart.querySelectorAll("[data-minicart-remove]");
    const discountInput = minicart.querySelector(
      "[data-minicart-discount-input]",
    );
    const discountApply = minicart.querySelector(
      "[data-minicart-discount-apply]",
    );
    const sizeSelects = minicart.querySelectorAll(
      "[data-minicart-size-select]",
    );
    const checkoutForm = minicart.querySelector("form.minicart__checkout-form");

    // Reconciliation keeps untouched row nodes alive across refreshes, so
    // every per-element binding below is guarded (§8.2) — only elements
    // freshly rendered by the server get bound.
    const shouldBind = (el) => {
      if (el.dataset.initialised) return false;
      el.dataset.initialised = "true";
      return true;
    };

    // ── Size select (merges duplicate-variant lines; serialised) ──
    sizeSelects.forEach((select) => {
      if (!shouldBind(select)) return;
      select.addEventListener("change", () => {
        const newVariantId = select.value;
        const item = select.closest("[data-id]");
        const lineKey = item?.dataset.key || item?.dataset.id;
        if (!newVariantId || !item || !lineKey) return;
        if (newVariantId === select.dataset.currentVariantId) return; // no-op guard

        const baseVariantId =
          select.getAttribute("data-current-variant-id") || select.value;

        // Register a size op (§3.1) so a concurrent refresh (e.g. a queued
        // quantity flush on another line, or cart:added) recognises this
        // row as foreign in-flight work and leaves it alone (D2).
        //
        // Stated exception to "mutate in place": this overwrites any live
        // pending/inflight quantity or remove op already registered for
        // lineKey rather than merging into it. Harmless today — the user's
        // qty intent survives via the optimistic input value that
        // targetQty is read from below, and an in-flight batch that no
        // longer finds its op for this line at settle time is a silent
        // no-op — but a size op always wins the slot. Keep this in mind
        // when porting (T8): do not let it become a genuine merge without
        // re-checking that assumption.
        lineOps.set(lineKey, {
          key: lineKey,
          kind: "size",
          targetQty: null,
          targetVariantId: newVariantId,
          baseQty: null,
          baseVariantId,
          state: "inflight",
          generation: 0,
          snapshotGeneration: 0,
          batchId: null,
          rowEl: item,
        });
        item.dataset.pending = "true";
        syncBusyUI();

        const qtyInput = item.querySelector("[data-minicart-quantity-input]");
        const currentQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

        // Merge into an existing line of the target variant, if any.
        let targetQty = currentQty;
        const existing = minicart.querySelector(
          `.minicart__item[data-id="${CSS.escape(newVariantId)}"]`,
        );
        if (existing && existing !== item) {
          const eq = existing.querySelector("[data-minicart-quantity-input]");
          if (eq) targetQty += parseInt(eq.value, 10) || 0;
        }

        const updates = {};
        updates[lineKey] = 0;
        updates[newVariantId] = targetQty;

        queueCartMutation(async () => {
          const token = ++refreshRequestToken;
          const data = await cartFetch("cart/update.js", { updates });
          const op = lineOps.get(lineKey);
          lineOps.delete(lineKey);
          if (!data || data.error) {
            if (op) revertOptimisticUpdate(op);
            else delete item.dataset.pending;
            await refreshMinicart(minicart, null, null, null, token);
            return;
          }
          await refreshMinicart(minicart, null, data.sections, data, token);
        });
      });
    });

    // ── Discount panel open/close ──
    const openDiscountNote = () => {
      if (!discountInput) return;
      if (!discountInput.hasAttribute("data-error")) {
        setDiscountUiState({ open: true, isError: false });
      }
    };

    const closeDiscountNote = () => {
      if (!discountInput) return;
      setTimeout(() => {
        if (discountInput === document.activeElement) return;
        if (discountInput.hasAttribute("data-loading")) return;
        if (discountInput.hasAttribute("data-error")) return;
        const hasValue = discountInput.value.trim().length > 0;
        setDiscountUiState({ open: hasValue, isError: false });
      }, 150);
    };

    discountInput?.addEventListener("focus", openDiscountNote);
    discountInput?.addEventListener("blur", closeDiscountNote);

    const handleDiscountSubmit = () => {
      if (!discountInput || !discountApply) return;
      if (discountApply.disabled) return;
      const code = discountInput.value.trim();
      if (code) {
        const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
        setDiscountUiState({ open: isTouchDevice, isError: false });
        if (!isTouchDevice) discountInput.blur();
        updateDiscountAndRefresh(code, minicart);
      }
    };

    discountInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleDiscountSubmit();
      }
    });

    discountApply?.addEventListener("click", (e) => {
      e.preventDefault();
      handleDiscountSubmit();
    });
    discountApply?.addEventListener("mousedown", (e) => {
      e.preventDefault();
    });

    discountInput?.addEventListener("input", () => {
      const hasValue = discountInput.value.trim().length > 0;
      if (discountApply) discountApply.disabled = !hasValue;
      if (discountErrorTimer || discountInput.hasAttribute("data-error")) {
        if (discountErrorTimer) {
          clearTimeout(discountErrorTimer);
          discountErrorTimer = null;
        }
        setDiscountUiState({ open: hasValue, isError: false });
      }
    });

    // ── Quantity input (direct, clamped to stock) ──
    quantityInputs.forEach((input) => {
      if (!shouldBind(input)) return;
      input.addEventListener("change", () => {
        const itemEl = input.closest("[data-id]");
        const lineKey = itemEl?.dataset.key;
        if (!lineKey) return;
        const parsed = parseInt(input.value, 10);
        if (isNaN(parsed)) {
          // Non-numeric ⇒ no-op; restore the last known quantity.
          const prev =
            itemEl.querySelector("[data-minicart-quantity]")?.dataset
              .currentQty ?? "1";
          input.value = prev;
          return;
        }
        // Over stock: reject the whole change — keep the current quantity and
        // tell them, so nothing is committed only to be dropped at checkout.
        const max = parseInt(itemEl.dataset.max, 10);
        if (!isNaN(max) && parsed > max) {
          const prev =
            itemEl.querySelector("[data-minicart-quantity]")?.dataset
              .currentQty ?? "1";
          input.value = prev;
          showItemError(
            lineKey,
            getHeader()?.dataset.textQuantityAdjusted ||
              "Not enough stock available.",
          );
          return;
        }
        const clamped = Math.min(99, Math.max(0, parsed));
        input.value = String(clamped);
        queueQuantity(itemEl, lineKey, clamped);
      });
    });

    // ── Quantity buttons ──
    quantityBtns.forEach((button) => {
      if (!shouldBind(button)) return;
      button.addEventListener("click", () => {
        const currentQty = parseInt(button.dataset.currentQty, 10);
        const itemEl = button.closest("[data-id]");
        const lineKey = itemEl?.dataset.key;
        if (!lineKey) return;
        const isAdd = button.dataset.function === "add";
        let newQty = isAdd ? currentQty + 1 : currentQty - 1;
        if (isAdd) {
          // Refuse to exceed available stock, with a message — the shopper
          // can't push past the ceiling and get their units removed at checkout.
          const max = parseInt(itemEl.dataset.max, 10);
          if (!isNaN(max) && newQty > max) {
            showItemError(
              lineKey,
              getHeader()?.dataset.textQuantityAdjusted ||
                "Not enough stock available.",
            );
            return;
          }
          newQty = Math.min(99, newQty);
        }
        queueQuantity(itemEl, lineKey, newQty);
      });
    });

    // ── Remove buttons ──
    removeBtns.forEach((button) => {
      if (!shouldBind(button)) return;
      button.addEventListener("click", () => {
        const itemEl = button.closest("[data-id]");
        const lineKey = itemEl?.dataset.key;
        if (!lineKey) return;
        queueQuantity(itemEl, lineKey, 0);
      });
    });

    // ── Checkout integrity (B4 + D10) — always clickable (§1 decision 4) ──
    // Checkout must never post a stale cart, but syncing must never make
    // the button unclickable. A busy click flushes pending work, shows a
    // visible awaiting state, then either proceeds or backs off leaving
    // the failure banner up. `requestSubmit(button)` (not `.submit()`)
    // preserves the button's `name="checkout"` submitter param, so a busy
    // click redirects to checkout instead of posting a bare cart update
    // (D10). requestSubmit() re-dispatches 'submit' through this same
    // listener; once genuinely idle it falls straight through the guard
    // above and the browser performs the real submission — a deliberate
    // pass-through, not runaway recursion, and it also re-flushes for
    // free if new work queued during the await. The form/button can be
    // replaced mid-await by a reconcile's footer patch (§3.5), so both
    // are re-resolved live afterwards rather than trusted from closure.
    if (checkoutForm && !checkoutForm.dataset.submitBound) {
      checkoutForm.dataset.submitBound = "true";
      checkoutForm.addEventListener("submit", async (e) => {
        if (!isCartBusy()) return; // idle cart: normal, unmodified single POST
        e.preventDefault();

        const clickedButton = checkoutForm.querySelector(
          ".minicart__checkout-button",
        );
        if (clickedButton?.dataset.awaiting === "true") return; // already awaiting a prior click
        if (clickedButton) {
          clickedButton.dataset.awaiting = "true";
          clickedButton.setAttribute("aria-busy", "true");
        }

        flushQuantities();
        // runQuantityFlush never rejects — a failure resolves into a
        // banner instead (§3.2) — so await the shared chain, then read
        // whether a failure banner just surfaced from it.
        await mutationChain.catch(() => {});

        const liveMinicart = getMinicart();
        const liveForm =
          liveMinicart?.querySelector("form.minicart__checkout-form") ||
          checkoutForm;
        const liveButton = liveForm.querySelector(".minicart__checkout-button");
        const banner = liveMinicart?.querySelector("[data-minicart-banner]");
        const flushFailed = banner ? banner.hidden === false : false;

        if (liveButton) {
          delete liveButton.dataset.awaiting;
          liveButton.removeAttribute("aria-busy");
        }

        if (flushFailed) return; // leave the banner up; nothing to submit yet

        liveForm.requestSubmit(liveButton || undefined);
      });
    }

    initRecommendationSlider();
  }

  /**
   * Wire the external prev/next chevrons in the minicart's
   * "You might also like" header to the inner
   * `<athos-carousel>` element. The component exposes
   * `prev()` / `next()` and a `data-disabled` attribute when
   * the rail is underfilled. If the Searchspring profile
   * returns no results, hide the entire slider section.
   */
  function initRecommendationSlider() {
    const slider = document.querySelector('[data-js="minicart-slider"]');
    if (!slider || slider.dataset.initialised === "true") return;
    slider.dataset.initialised = "true";

    const carousel = slider.querySelector(
      '[data-js="minicart-slider-carousel"]',
    );
    const prevBtn = slider.querySelector("[data-minicart-slider-prev]");
    const nextBtn = slider.querySelector("[data-minicart-slider-next]");

    if (!carousel) return;

    prevBtn?.addEventListener("click", () => {
      if (typeof carousel.prev === "function") carousel.prev();
    });
    nextBtn?.addEventListener("click", () => {
      if (typeof carousel.next === "function") carousel.next();
    });

    carousel.addEventListener("carousel:empty", () => {
      slider.hidden = true;
    });

    carousel.addEventListener("carousel:initialised", () => {
      if (carousel.dataset.cardsResolved === "true") return;
      carousel.dataset.cardsResolved = "true";

      const track = carousel.querySelector('[data-js="carousel-track"]');
      if (!track) return;

      // Coordinates with the product-card entrance fade defined in
      // product-card.liquid; 700ms covers the longest staggered card delay.
      track.dataset.cardsResolving = "true";
      setTimeout(() => {
        delete track.dataset.cardsResolving;
      }, 700);
    });
  }

  /**
   * One-tap add for a minicart recommendation card: add its first available
   * variant straight to the bag and refresh the drawer in place. The card
   * exposes `data-variant-id` (bound by the carousel when size data exists);
   * otherwise we resolve the first available variant from the product JSON.
   * @param {HTMLElement} card - a [data-quick-add-first] product card
   */
  const addRecommendationToCart = async (card) => {
    if (!card || card.dataset.adding === "true") return;
    const handle = card.getAttribute("data-product-handle");
    let variantId = card.getAttribute("data-variant-id");
    card.dataset.adding = "true";

    try {
      if (!variantId && handle) {
        const res = await fetch(
          `${window.Shopify.routes.root}products/${handle}.js`,
        );
        if (res.ok) {
          const product = await res.json();
          const variants = product.variants || [];
          const chosen = variants.find((v) => v.available) || variants[0];
          variantId = chosen ? String(chosen.id) : "";
        }
      }
      if (!variantId) throw new Error("no-variant");

      // Serialise with the rest of the pipeline — never race a quantity
      // flush — and refresh inside the same queue slot so no other queued
      // write can interleave between the add landing and the DOM update.
      // queueCartMutation's own mutationInFlight counter drives the busy
      // UI (syncBusyUI) — no separate manual arming needed here.
      const result = await queueCartMutation(async () => {
        const data = await cartFetch("cart/add.js", {
          id: variantId,
          quantity: 1,
        });
        if (!data || data.error) return data;
        const token = ++refreshRequestToken;
        await refreshMinicart(getMinicart(), null, data.sections, data, token);
        return data;
      });
      if (!result || result.error) {
        showBannerError(
          getHeader()?.dataset.textAddError || "Could not add to bag",
        );
        return;
      }

      // Let Athos attribute the add-to-cart to this recommendation.
      card.dispatchEvent(
        new CustomEvent("quick-add:added", {
          bubbles: true,
          detail: { item: result },
        }),
      );
    } catch {
      showBannerError(
        getHeader()?.dataset.textAddError || "Could not add to bag",
      );
    } finally {
      delete card.dataset.adding;
    }
  };

  // ── Orchestration — discount flow ──────────────────────────

  const setDiscountLoading = (loading) => {
    const input = document.querySelector("[data-minicart-discount-input]");
    const tag = document.querySelector("[data-minicart-discount-remove]");
    const applyBtn = document.querySelector("[data-minicart-discount-apply]");
    const target = input || tag;
    if (!target) return;

    if (loading) {
      target.setAttribute("data-loading", "true");
      if (input) input.disabled = true;
      if (applyBtn) {
        applyBtn.setAttribute("data-loading", "true");
        applyBtn.disabled = true;
      }
    } else {
      target.removeAttribute("data-loading");
      if (input) input.disabled = false;
      if (applyBtn) {
        applyBtn.removeAttribute("data-loading");
        applyBtn.disabled = !input?.value.trim();
      }
    }
  };

  // ── Shipping-code eligibility probe ────────────────────────

  const STOREFRONT_API_ENDPOINT = "/api/2026-04/graphql.json";

  // Markets whose shipping zones only resolve once a postcode is known;
  // other countries generate delivery options from the country alone.
  const PROBE_POSTCODES = {
    US: "10001",
    CA: "M5V 2T6",
    GB: "SW1A 1AA",
    AU: "2000",
    NZ: "6011",
  };

  /** Eligibility verdict promises keyed on code + line set, so concurrent
   *  callers and later re-syncs of an unchanged cart share one probe. */
  const eligibilityCache = new Map();

  /**
   * Ask whether a discount code's conditions hold for the given cart
   * lines. A shipping discount is evaluated by a Shopify Function that
   * only runs once delivery options exist, so the Ajax cart's
   * `applicable` flag merely confirms the code exists and is active. The
   * real evaluation is reproduced on a throwaway Storefront API cart
   * carrying the same lines and a representative delivery address.
   * Anything short of a definitive refusal (delivery options generated
   * and applicable still false) resolves 'unknown' — the probe cart is
   * anonymous, so it must never veto a customer-scoped code.
   * @param {string} code - The applied discount code.
   * @param {Array<Object>} items - Ajax cart items ({ variant_id, quantity }).
   * @returns {Promise<string>} 'applicable' | 'inapplicable' | 'unknown'
   */
  const probeDiscountEligibility = (code, items) => {
    const token = window.themeSettings?.storefrontApiToken;
    const country = window.themeSettings?.country;
    if (!token || !country || !items?.length) return Promise.resolve("unknown");

    const key = `${code.toLowerCase()}|${items
      .map((i) => `${i.variant_id}x${i.quantity}`)
      .sort()
      .join(",")}`;
    const cached = eligibilityCache.get(key);
    if (cached) return cached;

    // Definitive verdicts persist for the tab session so navigation does
    // not re-probe an unchanged cart (the in-memory cache dies with the
    // page); 'unknown' is never persisted so a transient failure retries.
    let stored = null;
    try {
      stored = window.sessionStorage.getItem(`minicart-eligibility:${key}`);
    } catch {
      /* storage unavailable — probe instead */
    }
    if (stored === "applicable" || stored === "inapplicable")
      return Promise.resolve(stored);

    const gql = async (query, variables) => {
      const res = await fetch(STOREFRONT_API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables }),
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return null;
      const payload = await res.json();
      return payload?.data || null;
    };

    const probe = (async () => {
      try {
        const created = await gql(
          "mutation($input: CartInput!) { cartCreate(input: $input) { cart { id } } }",
          {
            input: {
              lines: items.map((i) => ({
                merchandiseId: `gid://shopify/ProductVariant/${i.variant_id}`,
                quantity: i.quantity,
              })),
              discountCodes: [code],
            },
          },
        );
        const cartId = created?.cartCreate?.cart?.id;
        if (!cartId) return "unknown";

        const deliveryAddress = { countryCode: country };
        if (PROBE_POSTCODES[country])
          deliveryAddress.zip = PROBE_POSTCODES[country];

        const addressed = await gql(
          `mutation($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
            cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
              cart {
                discountCodes { code applicable }
                deliveryGroups(first: 1) {
                  edges { node { deliveryOptions { handle } } }
                }
              }
            }
          }`,
          {
            cartId,
            addresses: [{ selected: true, address: { deliveryAddress } }],
          },
        );
        const cart = addressed?.cartDeliveryAddressesAdd?.cart;
        const optionCount =
          cart?.deliveryGroups?.edges?.[0]?.node?.deliveryOptions?.length || 0;
        const entry = (cart?.discountCodes || []).find(
          (d) => d.code?.toLowerCase() === code.toLowerCase(),
        );
        // No delivery options means the discount function never ran and
        // the flag still reflects bare code existence — not a verdict.
        if (!optionCount || !entry) return "unknown";
        return entry.applicable ? "applicable" : "inapplicable";
      } catch {
        return "unknown";
      }
    })().then((verdict) => {
      if (verdict !== "unknown") {
        try {
          window.sessionStorage.setItem(`minicart-eligibility:${key}`, verdict);
        } catch {
          /* noop */
        }
      }
      return verdict;
    });

    eligibilityCache.set(key, probe);
    return probe;
  };

  /**
   * Surface a shipping-only discount code that Liquid cannot see. Such a
   * code has no allocation pre-checkout, so cart.discount_applications
   * omits it and the server renders the entry form; the Ajax cart's
   * discount_codes list is the only surface that reports it. Confirms
   * eligibility first — the list's `applicable` flag does not evaluate
   * the code's conditions against this cart — then swaps the entry form
   * for the removable code tag and reveals the success note. A
   * definitively ineligible code is detached from the cart instead:
   * rendering its tag would read as applied while checkout charges full
   * shipping.
   * @param {Object} cartData - An Ajax cart payload (cart.js shape).
   * @returns {void}
   */
  const syncShippingDiscountTag = (cartData) => {
    const minicart = getMinicart();
    if (!minicart) return;
    const form = minicart.querySelector(".minicart__discount-form");
    const template = minicart.querySelector(
      "template[data-minicart-shipping-discount]",
    );
    if (!form || !template) return;

    const applied = (cartData?.discount_codes || []).find((d) => d.applicable);
    if (!applied) return;

    probeDiscountEligibility(applied.code, cartData?.items || []).then(
      (verdict) => {
        // A newer render superseding this one replaces the form — that
        // render's own sync call carries on from here.
        if (!form.isConnected) return;

        if (verdict === "inapplicable") {
          queueCartMutation(() => applyDiscountCode(""));
          return;
        }

        const tag = template.content.firstElementChild?.cloneNode(true);
        if (!tag) return;
        tag.dataset.discountCode = applied.code;
        const removeLabel = tag.getAttribute("aria-label");
        if (removeLabel)
          tag.setAttribute(
            "aria-label",
            removeLabel.replace("__CODE__", applied.code),
          );
        tag.childNodes.forEach((node) => {
          if (
            node.nodeType === Node.TEXT_NODE &&
            node.nodeValue.includes("__CODE__")
          ) {
            node.nodeValue = applied.code;
          }
        });
        form.replaceWith(tag);

        const note = minicart.querySelector(
          "[data-minicart-shipping-discount-note]",
        );
        if (note) note.hidden = false;

        const panel = minicart.querySelector("[data-minicart-discount-panel]");
        if (panel) panel.hidden = true;

        // The amount slot stays empty for a shipping-only code but still
        // contributes the row's flex gap — drop it so the tag sits flush.
        const amount = tag
          .closest(".minicart__breakdown-item")
          ?.querySelector(".minicart__total-price");
        if (amount && !amount.textContent.trim()) amount.hidden = true;
      },
    );
  };

  /**
   * Apply or remove a discount code. The whole fetch+refresh flow is
   * queued onto the shared mutation chain and refreshRequestToken is claimed AFTER this call's own fetch
   * resolves, not at click time — claiming it early let a slower-to-
   * settle mutation queued behind this one bump the token first, so this
   * call's own later refresh lost the staleness check and got silently
   * dropped (D6).
   * @param {string} discount - Discount code, or '' to remove the current one.
   * @param {HTMLElement} minicart
   * @returns {Promise<void>}
   */
  const updateDiscountAndRefresh = (discount, minicart) => {
    setDiscountLoading(true);

    if (discountErrorTimer) {
      clearTimeout(discountErrorTimer);
      discountErrorTimer = null;
    }

    const showError = (msg) => {
      setDiscountUiState({ open: true, isError: true, message: msg });
      setDiscountLoading(false);

      discountErrorTimer = setTimeout(() => {
        setDiscountUiState({ open: false, isError: false });
        const { input } = getDiscountUiElements();
        if (input) {
          input.value = "";
          const btn = document.querySelector("[data-minicart-discount-apply]");
          if (btn) btn.disabled = true;
        }
        discountErrorTimer = null;
      }, 5000);
    };

    return queueCartMutation(async () => {
      if (discount === "") {
        const data = await applyDiscountCode("");
        if (data && !data.error) {
          const token = ++refreshRequestToken;
          await refreshMinicart(minicart, null, data.sections, data, token);
          clearStaleBanner(minicart);
        } else {
          const msg =
            getHeader()?.dataset.textDiscountRemoveError ||
            "Could not remove discount.";
          showError(msg);
        }
        return;
      }

      const cartData = await applyDiscountCode(discount);
      const invalidMsg =
        getHeader()?.dataset.textDiscountInvalid ||
        "This code is not valid, or may be store credit or a gift card.";

      if (!cartData || cartData.error) {
        showError(invalidMsg);
        return;
      }

      const wasApplied = cartData.discount_codes?.some(
        (d) => d.code.toLowerCase() === discount.toLowerCase() && d.applicable,
      );

      if (!wasApplied) {
        showError(invalidMsg);
        return;
      }

      // `applicable` alone only proves the code exists — a shipping code
      // whose conditions reject this cart still passes it. Confirm
      // eligibility before reporting success, and detach an ineligible
      // code so it never sits on the cart looking applied.
      const verdict = await probeDiscountEligibility(
        discount,
        cartData.items || [],
      );
      if (verdict === "inapplicable") {
        await applyDiscountCode("");
        showError(
          getHeader()?.dataset.textDiscountNotApplicable ||
            "This code doesn't apply to the items in your bag.",
        );
        return;
      }

      const token = ++refreshRequestToken;
      await refreshMinicart(minicart, null, cartData.sections, cartData, token);
      clearStaleBanner(minicart);
    });
  };

  // ── Cart API ───────────────────────────────────────────────

  /**
   * POST a cart endpoint with section-rendering params.
   * @param {string} endpoint
   * @param {Object} body
   * @returns {Promise<Object>} cart data or an internal error sentinel
   */
  const cartFetch = async (endpoint, body) => {
    try {
      const res = await fetch(`${window.Shopify.routes.root}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...body,
          sections: SECTIONS_TO_FETCH,
          sections_url: window.location.pathname,
        }),
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });

      if (!res.ok) return { error: "Network error", details: await res.text() };

      const data = await res.json();
      if (data?.status && data.status !== 200) {
        return {
          error: "Shopify error",
          details: data.message || data.description,
          data,
        };
      }
      return data;
    } catch (err) {
      // AbortSignal.timeout() raises a TimeoutError DOMException on the
      // signal firing; a manual abort would raise AbortError — treat both
      // as the same honest "took too long" sentinel. Never throws.
      if (err.name === "TimeoutError" || err.name === "AbortError")
        return { error: "Timeout error", timeout: true };
      return { error: "Unexpected error", details: err };
    }
  };

  // POST the discount mutation. Not queued itself — updateDiscountAndRefresh
  // queues its whole fetch+refresh flow as a single unit (see its own doc).
  const applyDiscountCode = (discount) =>
    cartFetch("cart/update.js", { discount });

  /**
   * Map a cartFetch error sentinel to a localised, user-facing message.
   * Keeps the internal sentinel strings out of the UI (H1).
   * @param {Object|null} data
   * @returns {string}
   */
  const extractErrorMessage = (data) => {
    const header = getHeader();
    const generic = header?.dataset.textUpdateError || "Updating cart failed.";
    if (!data || !data.error) return generic;
    if (data.error === "Network error")
      return header?.dataset.textErrorNetwork || generic;
    if (data.error === "Timeout error")
      return header?.dataset.textErrorTimeout || generic;
    if (data.error === "Unexpected error")
      return header?.dataset.textErrorGeneric || generic;
    // 'Shopify error' — details carries a specific server message.
    const detailStr = String(data.details || "").trim();
    if (detailStr) {
      if (detailStr.startsWith("{") || detailStr.startsWith("[")) {
        try {
          const parsed = JSON.parse(detailStr);
          if (parsed?.message) return parsed.message;
        } catch {
          /* fall through */
        }
      } else {
        return detailStr;
      }
    }
    return header?.dataset.textErrorGeneric || generic;
  };

  // ── Line-item reconciliation ───────────────────────────────
  // Patches the live items list against the server HTML row-by-row
  // (keyed on data-key) so untouched rows keep their DOM nodes. A blanket
  // replaceChildren re-inserts every row — which reads as a flash — and
  // discards any in-flight removal collapse.

  /**
   * Drop a row once its collapse has finished (immediately when it already
   * has, or under reduced motion where no collapse animation plays).
   * D9 fallback: disposal must not depend on animationend alone — a
   * display:none dialog never fires it — so a closed drawer or an
   * already-zero-height row disposes synchronously, and the animated path
   * carries a timeout fallback slightly above the collapse duration.
   * @param {HTMLElement} row
   */
  const removeRowWhenCollapsed = (row) => {
    const minicart = getMinicart();
    if (
      prefersReducedMotion ||
      row.dataset.collapsed ||
      !minicart?.open ||
      row.offsetHeight === 0
    ) {
      row.remove();
      return;
    }
    let fallback;
    const dispose = () => {
      clearTimeout(fallback);
      row.removeEventListener("animationend", onGone);
      if (row.isConnected) row.remove();
    };
    function onGone(e) {
      if (e.animationName !== "minicart-item-collapse") return;
      dispose();
    }
    row.addEventListener("animationend", onGone);
    if (!row.dataset.removing) animateItemRemoval(row);
    fallback = setTimeout(dispose, 400); // above the ~300ms collapse duration
  };

  /**
   * Sync one live row to its freshly-rendered server counterpart:
   * attributes and children are replaced wholesale, but an already-hydrated
   * image whose artwork is unchanged is carried over so it never flashes
   * back to a skeleton.
   * @param {HTMLElement} row - the live row
   * @param {HTMLElement} newRow - the server-rendered row (parsed, inert)
   */
  const patchRow = (row, newRow) => {
    const liveImage = row.querySelector(".minicart__product-image");
    const liveSrc = liveImage?.querySelector("img")?.getAttribute("src");

    [...row.attributes].forEach((attr) => {
      if (!newRow.hasAttribute(attr.name)) row.removeAttribute(attr.name);
    });
    [...newRow.attributes].forEach((attr) =>
      row.setAttribute(attr.name, attr.value),
    );
    row.replaceChildren(...newRow.childNodes);

    const tpl = row.querySelector("template[data-minicart-image-template]");
    const tplSrc = tpl?.content.querySelector("img")?.getAttribute("src");
    if (liveImage && tpl && tplSrc && tplSrc === liveSrc) {
      tpl.replaceWith(liveImage);
    }
  };

  /**
   * Look up a line's live op by key, falling back to a variant-id match
   * (size ops carry the row's old/new variant id, not its line key). The
   * single lookup every reconcile skip-guard runs through (§3.5).
   * @param {string} key
   * @param {string} [variantId]
   * @returns {Object|null}
   */
  const findOp = (key, variantId) => {
    if (key && lineOps.has(key)) return lineOps.get(key);
    if (!variantId) return null;
    for (const op of lineOps.values()) {
      if (op.targetVariantId === variantId || op.baseVariantId === variantId)
        return op;
    }
    return null;
  };

  /**
   * Reconcile the live items list with the server-rendered one. Unchanged
   * rows are left untouched (change detection via a stored pristine clone
   * of the last server render), changed rows are patched in place, rows
   * missing from the server collapse out, and new rows are adopted at
   * their server position. A row backed by a live op is skipped UNLESS
   * this refresh is that op's own settling batch (§3.5) — foreign
   * in-flight work is never touched; reassertOpVisuals() re-asserts every
   * live op's state after every reconcile.
   *
   * Matching is by line key first, then by variant id: Shopify can
   * regenerate a line's key on a quantity update, and the variant-id
   * fallback keeps that line patching in place instead of collapsing out
   * while a "new" duplicate row is inserted beside it.
   * @param {HTMLElement} currentList - the live .minicart__items element
   * @param {HTMLElement} newList - the server-rendered .minicart__items
   * @param {number|null} settlingBatchId - the batch this refresh is settling, if any
   */
  const reconcileItems = (currentList, newList, settlingBatchId = null) => {
    const newRows = [...newList.children].filter((el) =>
      el.classList.contains("minicart__item"),
    );
    const newKeys = new Set(newRows.map((row) => row.dataset.key));
    const liveRows = [...currentList.children].filter((el) =>
      el.classList.contains("minicart__item"),
    );
    const claimed = new Set();

    const isForeignWork = (row) => {
      const op = findOp(row.dataset.key, row.dataset.id);
      return (
        !!op && !(op.state === "settling" && op.batchId === settlingBatchId)
      );
    };

    const matchLive = (newRow) => {
      const byKey = liveRows.find(
        (row) => !claimed.has(row) && row.dataset.key === newRow.dataset.key,
      );
      if (byKey) return byKey;
      return (
        liveRows.find(
          (row) =>
            !claimed.has(row) &&
            !newKeys.has(row.dataset.key) &&
            !row.dataset.removing &&
            row.dataset.id === newRow.dataset.id,
        ) || null
      );
    };

    // Pair every server row with a live row before deciding removals.
    const pairs = newRows.map((newRow) => {
      const live = matchLive(newRow);
      if (live) claimed.add(live);
      return { newRow, live };
    });

    // Live rows the server no longer has collapse out (rows mid-collapse
    // are dropped once their animation finishes) — unless the row is
    // foreign in-flight work, in which case it's left alone entirely.
    liveRows.forEach((row) => {
      if (claimed.has(row) || isForeignWork(row)) return;
      if (row.dataset.collapsed)
        row.remove(); // already collapsed — no second collapse (D9)
      else removeRowWhenCollapsed(row);
    });

    // Patch / adopt / order in the server's sequence.
    let cursor = currentList.firstElementChild;
    pairs.forEach(({ newRow, live }) => {
      while (cursor && !claimed.has(cursor)) cursor = cursor.nextElementSibling;
      const pristine = newRow.cloneNode(true);

      if (!live) {
        newRow.__serverNode = pristine;
        currentList.insertBefore(newRow, cursor);
        return;
      }

      if (live === cursor) cursor = cursor.nextElementSibling;
      else currentList.insertBefore(live, cursor);

      if (isForeignWork(live)) return; // skip: foreign in-flight work (§3.5)

      if (!live.__serverNode || !live.__serverNode.isEqualNode(pristine)) {
        patchRow(live, newRow);
        live.__serverNode = pristine;
      }
    });
  };

  // ── Focus management (§5, T5) ───────────────────────────────
  // reconcileItems()/patchRow() can destroy the DOM node a stepper,
  // remove button, or size select currently holds focus on (patchRow
  // replaces a row's children wholesale; a settled removal drops the row
  // entirely). captureRowFocus() records which row + control held focus
  // immediately before a body patch; restoreRowFocus() re-focuses the
  // equivalent control afterwards so keyboard/VoiceOver focus never
  // silently falls back to <body>.

  /**
   * Identify which of a row's known focusable controls an element is, as
   * a selector scoped to `.minicart__item` — stable across patchRow()
   * replacing the row's children with a freshly-parsed server copy.
   * @param {HTMLElement} el
   * @returns {string|null} null if el isn't one of the row's controls
   */
  const focusableRowControlSelector = (el) => {
    if (el.matches("[data-minicart-quantity]")) {
      return `[data-minicart-quantity][data-function="${el.dataset.function}"]`;
    }
    if (el.matches("[data-minicart-quantity-input]"))
      return "[data-minicart-quantity-input]";
    if (el.matches("[data-minicart-remove]")) return "[data-minicart-remove]";
    if (el.matches("[data-minicart-size-select]"))
      return "[data-minicart-size-select]";
    if (el.matches(".minicart__product-title"))
      return ".minicart__product-title";
    return null;
  };

  /**
   * Record which row + control holds focus before a body patch, so it can
   * be restored after (§5, T5). Ignores focus outside the items list
   * entirely — a refresh must never steal focus that wasn't already there.
   * @param {HTMLElement|null} body - the live .minicart__body element,
   *   read BEFORE reconcileItems/patchRow run
   * @returns { {lineKey: string, variantId: string, selector: string, rowIndex: number, nextKey: (string|null), prevKey: (string|null)} | null }
   */
  const captureRowFocus = (body) => {
    const active = document.activeElement;
    const itemsList = body?.querySelector(".minicart__items");
    if (!active || !itemsList || !itemsList.contains(active)) return null;
    const row = active.closest(".minicart__item");
    if (!row) return null;
    const selector = focusableRowControlSelector(active);
    if (!selector) return null;
    const rows = [...itemsList.children].filter((el) =>
      el.classList.contains("minicart__item"),
    );
    const rowIndex = rows.indexOf(row);
    return {
      lineKey: row.dataset.key,
      variantId: row.dataset.id,
      selector,
      rowIndex,
      nextKey: rows[rowIndex + 1]?.dataset.key || null,
      prevKey: rows[rowIndex - 1]?.dataset.key || null,
    };
  };

  /**
   * Restore focus captured by captureRowFocus() once a body patch has
   * landed (§5, T5). Same line still present (key, then variant-id
   * fallback — a quantity settle can regenerate a line's key, same
   * fallback reconcileItems' own matching uses) → its equivalent control.
   * Line genuinely gone (a settled removal — rare here in practice, since
   * relocateFocusForRemoval() already moves focus at intent time, but kept
   * as a defensive rung) → the row that was next, else the row that was
   * previous (by their own captured keys, then position), else the empty
   * state's primary link, else the dialog title. Every rung is verified —
   * `.focus()` on a display:none target (a row mid-removal, a hidden empty
   * state) silently no-ops, so each candidate is checked and skipped
   * rather than trusted blind.
   * @param {ReturnType<typeof captureRowFocus>} captured
   */
  const restoreRowFocus = (captured) => {
    if (!captured) return;
    const minicart = getMinicart();
    if (!minicart) return;

    const findRow = (key, variantId) =>
      (key &&
        minicart.querySelector(
          `.minicart__item[data-key="${CSS.escape(key)}"]`,
        )) ||
      (variantId &&
        minicart.querySelector(
          `.minicart__item[data-id="${CSS.escape(variantId)}"]`,
        )) ||
      null;

    // Verified focus attempt — returns whether focus actually landed on el,
    // since .focus() on a hidden/display:none/disabled target is a silent no-op.
    const tryFocus = (el) => {
      if (!el) return false;
      el.focus({ preventScroll: true });
      return document.activeElement === el;
    };

    const isLive = (row) =>
      row && !row.dataset.removing && !row.dataset.collapsed;

    const sameRow = findRow(captured.lineKey, captured.variantId);
    if (isLive(sameRow)) {
      const control = sameRow.querySelector(captured.selector);
      if (tryFocus(control) || tryFocus(sameRow)) return;
    }

    const itemsList = minicart.querySelector(".minicart__items");
    const rows = itemsList
      ? [...itemsList.children].filter((el) =>
          el.classList.contains("minicart__item"),
        )
      : [];
    const fallbackRows = [
      findRow(captured.nextKey, null),
      findRow(captured.prevKey, null),
      rows[captured.rowIndex],
      rows[captured.rowIndex - 1],
    ].filter(isLive);
    for (const fallbackRow of fallbackRows) {
      const control = fallbackRow.querySelector("[data-minicart-remove]");
      if (tryFocus(control) || tryFocus(fallbackRow)) return;
    }

    const emptyState = minicart.querySelector("[data-minicart-empty-state]");
    const emptyLink =
      emptyState && !emptyState.hidden
        ? emptyState.querySelector("a, button")
        : null;
    if (tryFocus(emptyLink)) return;

    const title = minicart.querySelector("#minicart-title");
    tryFocus(title);
  };

  async function refreshMinicart(
    minicart,
    errorData = null,
    prefetchedSections = null,
    cartData = null,
    externalToken = null,
    settlingBatchId = null,
  ) {
    const currentToken = externalToken || ++refreshRequestToken;
    try {
      let minicartHTML;

      if (prefetchedSections?.[SECTION_ID]) {
        minicartHTML = prefetchedSections[SECTION_ID];
      } else {
        minicartHTML = await fetch(
          `${window.Shopify.routes.root}?section_id=${SECTION_ID}`,
        ).then((r) => r.text());
      }

      if (currentToken !== refreshRequestToken) return;

      const parser = new DOMParser();
      const minicartDoc = parser.parseFromString(minicartHTML, "text/html");

      const newMinicart = minicartDoc.querySelector(".minicart");
      const newCartBtn = minicartDoc.querySelector(".minicart__cart-button");
      const currentCartBtn = document.querySelector(".icons__cart");

      if (newMinicart) {
        const currentMinicart = getMinicart();
        if (!currentMinicart) return;

        // Update header (children only — the <header> keeps its data-text-* config)
        const newHeader = newMinicart.querySelector(".minicart__header");
        const currentHeader =
          currentMinicart.querySelector(".minicart__header");
        if (newHeader && currentHeader) {
          currentHeader.replaceChildren(...newHeader.childNodes);
        }

        // Update body (items / empty state), preserving slider content
        const newBody = newMinicart.querySelector(".minicart__body");
        const currentBody = currentMinicart.querySelector(".minicart__body");
        // Snapshot focus BEFORE any row is patched/removed below (§5, T5).
        const focusCapture = captureRowFocus(currentBody);
        const hadItems = !!currentBody?.querySelector(".minicart__items");
        // Clear any optimistic items→empty hide before reconciling.
        currentBody?.removeAttribute("data-emptying");
        const newItemsList = newBody?.querySelector(".minicart__items");
        const currentItemsList = currentBody?.querySelector(".minicart__items");

        if (newItemsList && currentItemsList) {
          // Items → items: patch rows in place instead of a blanket body
          // swap, so untouched rows (and the shipping bar, empty state and
          // recommendations rail) keep their live DOM nodes — no flash.
          currentBody.className = newBody.className;
          reconcileItems(currentItemsList, newItemsList, settlingBatchId);

          // Re-assert the non-empty chrome in case an optimistic
          // items→empty transition (a failed last-line removal) had fired.
          const emptyState = currentBody.querySelector(
            "[data-minicart-empty-state]",
          );
          if (emptyState) emptyState.hidden = true;
          const liveShippingBar =
            currentMinicart.querySelector(".shipping-bar");
          if (liveShippingBar) liveShippingBar.hidden = false;
        } else if (newBody && currentBody) {
          // Preserve the live shipping bar element. Section Rendering API
          // responses can omit this snippet's output (it depends on a
          // metaobject reference), so we keep the existing one and rely on
          // syncShippingBar() to update its progress.
          const preservedShippingBar =
            currentBody.querySelector(".shipping-bar");
          if (preservedShippingBar) preservedShippingBar.remove();

          const preservedEmptyState = currentBody.querySelector(
            "[data-minicart-empty-state]",
          );
          if (preservedEmptyState) preservedEmptyState.remove();

          // Only preserve the recommendations slider when the new state
          // still has items. Going from items → empty must drop the slider
          // so the empty-state markup renders cleanly.
          const newHasItems = !!newBody.querySelector(".minicart__items");

          const sliderFragment = document.createDocumentFragment();
          let hasSlider = false;
          if (newHasItems) {
            const end = currentBody.querySelector(".minicart__items");
            if (end) {
              while (end.nextSibling) {
                sliderFragment.appendChild(end.nextSibling);
                hasSlider = true;
              }
            }
          }

          currentBody.className = newBody.className;
          currentBody.replaceChildren(...newBody.childNodes);

          if (hasSlider) {
            const newEnd = currentBody.querySelector(".minicart__items");
            if (newEnd) {
              while (newEnd.nextSibling) newEnd.nextSibling.remove();
              currentBody.appendChild(sliderFragment);
            }
          }

          // Re-insert the preserved shipping bar if the new HTML didn't render one.
          if (
            preservedShippingBar &&
            !currentBody.querySelector(".shipping-bar")
          ) {
            currentBody.insertBefore(
              preservedShippingBar,
              currentBody.firstChild,
            );
          }

          // Toggle bar visibility based on whether the cart now has items.
          const liveShippingBar =
            currentMinicart.querySelector(".shipping-bar");
          if (liveShippingBar) {
            liveShippingBar.hidden = !newHasItems;
          }

          if (preservedEmptyState) {
            const newEmptyState = currentBody.querySelector(
              "[data-minicart-empty-state]",
            );
            if (newEmptyState) {
              currentBody.replaceChild(preservedEmptyState, newEmptyState);
            } else {
              currentBody.insertBefore(
                preservedEmptyState,
                currentBody.firstChild,
              );
            }
            preservedEmptyState.hidden = newHasItems;

            // Empty-state crossfade (§5.9d) — only on a real items → empty flip.
            if (!newHasItems && hadItems && !prefersReducedMotion) {
              preservedEmptyState.setAttribute("data-entering", "true");
              preservedEmptyState.addEventListener(
                "animationend",
                () => preservedEmptyState.removeAttribute("data-entering"),
                { once: true },
              );
            }
          }
        }

        // Restore focus captured before the body patch above (§5, T5).
        restoreRowFocus(focusCapture);

        // Update footer breakdown & checkout, preserving trust items
        const newFooter = newMinicart.querySelector("footer");
        const currentFooter = currentMinicart.querySelector("footer");

        if (newFooter && currentFooter) {
          // Clear any optimistic items→empty fade (e.g. a failed last-item
          // removal that the server bounced back) — the footer stays.
          currentFooter.removeAttribute("data-leaving");

          const newBreakdown = newFooter.querySelector(".minicart__breakdown");
          const currentBreakdown = currentFooter.querySelector(
            ".minicart__breakdown",
          );
          if (newBreakdown && currentBreakdown) {
            currentBreakdown.replaceChildren(...newBreakdown.childNodes);
          }

          const newCheckout = newFooter.querySelector(
            ".minicart__checkout-buttons",
          );
          const currentCheckout = currentFooter.querySelector(
            ".minicart__checkout-buttons",
          );
          if (newCheckout && currentCheckout) {
            currentCheckout.replaceChildren(...newCheckout.childNodes);
          }

          // Trust items (.minicart__trust-items) are intentionally not updated;
          // syncInstalments() keeps the BNPL amount fresh.
        } else if (newFooter && !currentFooter) {
          // Cart transitioned from empty → items: insert before the SR status node.
          const srNode = currentMinicart.querySelector(
            "[data-minicart-sr-status]",
          );
          const panel = currentMinicart.querySelector(".minicart__panel");
          if (panel) panel.insertBefore(newFooter, srNode || null);
        } else if (!newFooter && currentFooter) {
          // Cart transitioned from items → empty
          currentFooter.remove();
        }
      }

      if (newCartBtn && currentCartBtn) {
        // Clone, don't move: the header swap above already adopted
        // `newCartBtn` into the live drawer header, so spreading its
        // childNodes here would strip the drawer's bag icon + count.
        currentCartBtn.replaceChildren(
          ...[...newCartBtn.childNodes].map((node) => node.cloneNode(true)),
        );
      }

      // Refresh the page-header bag button's accessible name (its children
      // were just swapped, leaving its Liquid aria-label stale) and bump
      // the badges in both headers.
      const badge = document.querySelector("[data-minicart-count]");
      syncBagLabels(badge ? parseInt(badge.textContent, 10) || 0 : 0);
      bumpCartBadge();

      const activeMinicart = getMinicart();
      if (isMinicartOpen() && activeMinicart) {
        activeMinicart
          .querySelectorAll("template[data-minicart-image-template]")
          .forEach((tpl) => {
            tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
            tpl.remove();
          });
        window.themeSkeleton?.sweep(activeMinicart);
      }

      syncShippingDiscountTag(cartData);
      attachMinicartListeners();

      // Any optimistic change still pending stays visually ahead of this
      // snapshot, whoever triggered the refresh (e.g. a cart:added landing
      // while a quantity flush is queued).
      reassertOpVisuals();

      // Rail hydration on empty → items while open (idempotent) — B7.
      if (isMinicartOpen()) {
        const carousel = getMinicart()?.querySelector("athos-carousel");
        if (carousel && typeof carousel.forceInit === "function")
          carousel.forceInit();
      }

      // Sync shipping bar + instalments with the latest cart total.
      if (cartData && typeof cartData.total_price === "number") {
        totalsRequestToken += 1;
        syncShippingBar(cartData.total_price);
        syncInstalments(cartData.total_price);
      } else {
        fetchAndSyncCartTotals();
      }

      if (errorData?.message && newMinicart) {
        if (errorData.productId) {
          showItemError(errorData.productId, errorData.message);
        } else {
          showBannerError(errorData.message);
        }
      }

      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cartData } }),
      );
    } catch {
      // Errors silently caught — finally ensures UI cleanup.
    } finally {
      setDiscountLoading(false);
    }
  }

  /**
   * Recompute and set the page-header bag button's aria-label from a count.
   * The drawer-header bag button is re-rendered by Liquid on every refresh,
   * so only the persistent page-header button needs a JS re-set.
   * @param {number} count
   */
  const syncBagLabels = (count) => {
    const header = getHeader();
    if (!header) return;
    const empty = header.dataset.textBagLabelEmpty || "Bag, empty";
    const singular =
      header.dataset.textBagLabel || "Bag, \x7b\x7b count \x7d\x7d item";
    const plural =
      header.dataset.textBagLabelPlural || "Bag, \x7b\x7b count \x7d\x7d items";
    let label;
    if (!count || count <= 0) label = empty;
    else if (count === 1)
      label = singular.replace(/\x7b\x7b\s*count\s*\x7d\x7d/, count);
    else label = plural.replace(/\x7b\x7b\s*count\s*\x7d\x7d/, count);
    document
      .querySelectorAll("[data-minicart-toggle]")
      .forEach((btn) => btn.setAttribute("aria-label", label));
  };

  // ── Drawer lifecycle (primitive-driven) ────────────────────
  // The bottom-drawer primitive owns open/close, top-layer modality,
  // focus, overlay + scroll-lock. We hook its events for the minicart's
  // own open/close side-effects.

  document.addEventListener("drawer:open", (e) => {
    if (e.target?.id !== SECTION_ID) return;
    const minicart = getMinicart();
    if (!minicart) return;

    // Hydrate product images (a couple of frames after showModal — images
    // are skeletoned, so no flash).
    minicart
      .querySelectorAll("template[data-minicart-image-template]")
      .forEach((tpl) => {
        tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
        tpl.remove();
      });
    window.themeSkeleton?.sweep(minicart);

    const carousel = minicart.querySelector("athos-carousel");
    if (carousel && typeof carousel.forceInit === "function")
      carousel.forceInit();

    if (minicart.querySelector(".minicart__discount-form"))
      fetchAndSyncCartTotals();

    document
      .querySelectorAll("[data-minicart-toggle]")
      .forEach((btn) => btn.setAttribute("aria-expanded", "true"));

    // Dismiss the search surface / mega menu when the minicart opens.
    if (emit) {
      emit("search:close", {});
      emit("desktop-menu:close", {});
    }
  });

  document.addEventListener("drawer:close", (e) => {
    if (e.target?.id !== SECTION_ID) return;
    const minicart = getMinicart();
    if (!minicart) return;

    // Clear discount form state on close.
    const input = minicart.querySelector("[data-minicart-discount-input]");
    if (input) {
      input.value = "";
      input.blur();
      const applyBtn = minicart.querySelector("[data-minicart-discount-apply]");
      if (applyBtn) applyBtn.disabled = true;
    }
    setDiscountUiState({ open: false, isError: false });
    if (discountErrorTimer) {
      clearTimeout(discountErrorTimer);
      discountErrorTimer = null;
    }
  });

  document.addEventListener("drawer:closed", (e) => {
    if (e.target?.id !== SECTION_ID) return;
    document
      .querySelectorAll("[data-minicart-toggle]")
      .forEach((btn) => btn.setAttribute("aria-expanded", "false"));
  });

  // ── Header toggle shim ─────────────────────────────────────
  // The page-header cart button keeps data-minicart-toggle (header.liquid
  // is untouched) rather than the primitive's data-drawer-open, so bind a
  // delegated toggle. The drawer-internal buttons use data-drawer-close and
  // are handled by the primitive directly.
  document.addEventListener("click", (e) => {
    const toggle = e.target.closest("[data-minicart-toggle]");
    if (!toggle) return;
    if (!getMinicart() || !window.BottomDrawer) return;
    if (isMinicartOpen()) window.BottomDrawer.close(SECTION_ID);
    else window.BottomDrawer.open(SECTION_ID);
  });

  // ── Discount removal ───────────────────────────────────────
  document.addEventListener("click", (e) => {
    const removeTag = e.target.closest("[data-minicart-discount-remove]");
    if (!removeTag) return;
    const minicart = getMinicart();
    if (!minicart || !minicart.contains(removeTag)) return;
    updateDiscountAndRefresh("", minicart);
  });

  // ── Recommendation one-tap add ─────────────────────────────
  // Clicking a "You might also like" card adds its first variant straight
  // to the bag. Scoped to the minicart so other quick-add-first carousels
  // (PDP, search) keep their own behaviour.
  document.addEventListener("click", (e) => {
    const card = e.target.closest("[data-quick-add-first]");
    if (!card) return;
    const minicart = getMinicart();
    if (!minicart || !minicart.contains(card)) return;
    e.preventDefault();
    addRecommendationToCart(card);
  });

  // ── Cross-surface exclusivity ──────────────────────────────

  if (on) {
    // Close the minicart when a search surface or the mobile menu opens.
    on("search:open", () => {
      if (isMinicartOpen()) window.BottomDrawer?.close(SECTION_ID);
    });
    on("mobile-menu:toggle", (e) => {
      if (e.detail?.isOpen && isMinicartOpen())
        window.BottomDrawer?.close(SECTION_ID);
    });
  }

  // ── cart:added event ───────────────────────────────────────
  //
  // Producer `event.detail.sections` snapshots are deliberately IGNORED
  // here: they are rendered at the producer's add time, so when an add
  // races a minicart mutation (responses arrive out of order) a snapshot
  // can predate a removal and resurrect the deleted row. The refresh
  // always re-fetches inside the mutation queue instead — the freshest
  // cart state lands last. (sections/cart.liquid still consumes producer
  // snapshots for its own page section.)
  // Open unless `detail.openMinicart === false`; refresh regardless.

  const nextPaint = () =>
    new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );

  document.addEventListener("cart:added", async (event) => {
    const shouldOpen = event?.detail?.openMinicart !== false;
    if (shouldOpen && !isMinicartOpen()) window.BottomDrawer?.open(SECTION_ID);

    // Serialise with the internal mutation pipeline: flush any pending
    // quantity batch first so its write lands before this refresh, then
    // re-fetch fresh section HTML inside the queue (never a producer
    // snapshot — see the note above). queueCartMutation's own
    // mutationInFlight counter drives the busy UI (syncBusyUI).
    if (isCartBusy()) flushQuantities();
    await queueCartMutation(async () => {
      const token = ++refreshRequestToken;
      await refreshMinicart(getMinicart(), null, null, null, token);
    });
    await nextPaint();
  });

  // Quick-add error → surface inside the drawer via the banner UI.
  window.addEventListener("quick-add:error", (event) => {
    const fallback =
      getHeader()?.dataset.textAddError || "Could not add to bag";
    const msg = event?.detail?.message || fallback;
    showBannerError(msg);
  });

  // ── Theme-editor guards ────────────────────────────────────
  // Close the drawer if the section is unloaded/reloaded while open, so
  // the primitive's close path releases the scroll lock + overlay.

  document.addEventListener("shopify:section:unload", (e) => {
    if (!e.target?.querySelector?.(`#${SECTION_ID}`)) return;
    if (isMinicartOpen()) window.BottomDrawer?.close(SECTION_ID);
  });

  document.addEventListener("shopify:section:load", (e) => {
    if (e.target?.querySelector?.(`#${SECTION_ID}`)) attachMinicartListeners();
  });

  /**
   * Open the drawer when the page is requested with `?minicart=open`.
   *
   * The section is server-rendered with the current cart, so this opens the
   * primitive directly rather than dispatching `cart:added` — no refetch and
   * no mutation-queue round trip. The drawer's own `drawer:open` handler
   * still does the image/rail/totals hydration.
   *
   * The flag is stripped from the URL once consumed so a reload or a
   * back-navigation does not reopen the drawer; other params and the hash
   * are preserved.
   * @returns {void}
   */
  function openFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    if (params.get(OPEN_PARAM) !== OPEN_PARAM_VALUE) return;
    if (!getMinicart() || !window.BottomDrawer) return;

    params.delete(OPEN_PARAM);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );

    window.BottomDrawer.open(SECTION_ID);
  }

  // ── Initialise ─────────────────────────────────────────────

  attachMinicartListeners();

  // Deferred to DOMContentLoaded so every other deferred module (header
  // overlay, skeleton sweep, carousel) has registered before the drawer's
  // lifecycle events fire.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", openFromQueryString, {
      once: true,
    });
  } else {
    openFromQueryString();
  }
})();
